import React, { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * FilmSection — full-bleed cinematic block for the AfriMarket brand film.
 *
 * Behaviour:
 *  - Autoplays muted + loop + playsinline so mobile Safari + most browsers
 *    let it play without a user gesture.
 *  - Sound starts off. A pulsing "Tap for sound" badge invites un-muting.
 *  - Pause/Play, mute/unmute and fullscreen controls overlay the bottom-left.
 *  - Pauses automatically when scrolled out of view (saves bandwidth).
 */
const FilmSection = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [showSoundHint, setShowSoundHint] = useState(true);

  // Pause when off-screen, resume when scrolled back in.
  useEffect(() => {
    const el = containerRef.current;
    const v = videoRef.current;
    if (!el || !v) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            v.play().then(() => setPlaying(true)).catch(() => {});
          } else {
            v.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    setShowSoundHint(false);
    if (!v.muted) track("film_section_unmuted");
  };

  const goFullscreen = () => {
    const v = videoRef.current; if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
    else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
  };

  return (
    <section
      ref={containerRef}
      data-testid="film-section"
      className="relative bg-[#0B0E10] py-16 sm:py-20 overflow-hidden"
    >
      {/* Subtle warm halo behind the film frame */}
      <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-[#C05A3C]/12 to-transparent pointer-events-none" aria-hidden />
      <div className="container-narrow relative">
        <div className="text-center mb-8 sm:mb-10">
          <div className="label-overline text-[#D9923B] mb-3">Our story · 60 seconds</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
            From the heart of Africa<br className="hidden sm:block" /> to your front door.
          </h2>
          <p className="mt-4 text-white/70 text-sm sm:text-base max-w-2xl mx-auto font-sans-ui">
            A short film about the makers, growers, cooks and craftspeople behind every AfriMarket listing — and the diaspora who keep their cultures alive.
          </p>
        </div>

        <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 bg-black aspect-video">
          <video
            ref={videoRef}
            data-testid="film-video"
            src="/ads/afrimarket-film.mp4"
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
          />

          {/* Vignette */}
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-black/35 via-transparent to-transparent" aria-hidden />

          {/* Sound hint — pulses until first interaction */}
          {muted && showSoundHint && (
            <button
              type="button"
              data-testid="film-sound-hint"
              onClick={toggleMute}
              className="absolute top-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 hover:bg-white text-[#1A1A1A] text-xs font-semibold backdrop-blur-md shadow-lg animate-pulse"
            >
              <Volume2 className="w-3.5 h-3.5" /> Tap for sound
            </button>
          )}

          {/* Bottom-left control bar */}
          <div className="absolute bottom-3 left-3 sm:bottom-4 sm:left-4 flex items-center gap-1.5">
            <button
              type="button"
              data-testid="film-play-toggle"
              onClick={togglePlay}
              aria-label={playing ? "Pause film" : "Play film"}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center text-white backdrop-blur-md transition-colors"
            >
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-[1px]" />}
            </button>
            <button
              type="button"
              data-testid="film-mute-toggle"
              onClick={toggleMute}
              aria-label={muted ? "Unmute" : "Mute"}
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center text-white backdrop-blur-md transition-colors"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              type="button"
              data-testid="film-fullscreen"
              onClick={goFullscreen}
              aria-label="Fullscreen"
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 grid place-items-center text-white backdrop-blur-md transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FilmSection;
